import { useEffect, useRef } from "react";
import {
    Activity,
    LayoutTemplate,
    MousePointer2,
    Paintbrush,
    Palette,
    Ruler,
    Spline,
    Users,
    Wind,
} from "lucide-react";
import { CheckboxField } from "../reusable/CheckboxField";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { useFlowAgents, type ChildTrailCapMode } from "./FlowAgentsContext";
import { PresetSelector } from "../reusable/PresetSelector";
import { Switch } from "../reusable/Switch";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";
import { ColorPaletteEditor } from "../reusable/ColorPaletteEditor";
import { colorsToPaletteText, parsePaletteTextToColors } from "../../utils/pathAgents";
import {
    FLOW_ANGLE_NOISE_MODES,
    type FlowAngleNoiseMode,
} from "../../utils/flowAngleNoise";

export function FlowAgentsControls() {
    const {
        width,
        height,
        marginFactor,
        cellSize,
        seed,
        angleNoiseMode,
        angleNoiseOctaves,
        angleWarpStrength,
        angleWarpFrequency,
        noiseScale,
        stepNoiseScale,
        stepVariation,
        angleQuantizeDeg,
        fieldRotationDeg,
        mirrorNoiseX,
        mirrorNoiseY,
        uniformFlowBlend,
        uniformFlowAngleDeg,
        showFlowField,
        followSpeed,
        flowInfluence,
        maxTrailLength,
        maxTrailLengthRandomness,
        spawnChildIntervalSec,
        spawnChildIntervalRandomnessSec,
        childTrailCapMode,
        childTrailCapFixed,
        childTrailCapRandomness,
        stopAtMargin,
        groupSize,
        groupRadius,
        strokeLayoutCount,
        strokeLayoutSpacing,
        groupCohesionStrength,
        kickIntervalSec,
        kickIntervalRandomnessSec,
        forceMagnitude,
        velocityKickGradual,
        velocityKickBlendFrames,
        inputMode,
        setInputMode,
        strokeColor,
        strokePaletteText,
        setStrokePaletteText,
        randomStrokeColorPerAgent,
        setRandomStrokeColorPerAgent,
        strokeWidth,
        minStrokeWidth,
        initialStrokeWidth,
        strokeWidthStep,
        strokeWidthKickFrames,
        strokeWidthKickGradual,
        strokeWidthBlendFrames,
        trailTaperStartLength,
        trailTaperEndLength,
        smoothPaths,
        mergeTrailToSinglePath,
        filledVariableWidthStroke,
        unionGroupOnComplete,
        backgroundColor,
        exportBackground,
        completedPaths,
        activeAgents,
        setWidth,
        setHeight,
        setMarginFactor,
        setCellSize,
        setSeed,
        setAngleNoiseMode,
        setAngleNoiseOctaves,
        setAngleWarpStrength,
        setAngleWarpFrequency,
        setNoiseScale,
        setStepNoiseScale,
        setStepVariation,
        setAngleQuantizeDeg,
        setFieldRotationDeg,
        setMirrorNoiseX,
        setMirrorNoiseY,
        setUniformFlowBlend,
        setUniformFlowAngleDeg,
        setShowFlowField,
        setFollowSpeed,
        setFlowInfluence,
        setMaxTrailLength,
        setMaxTrailLengthRandomness,
        setGroupSize,
        setGroupRadius,
        setStrokeLayoutCount,
        setStrokeLayoutSpacing,
        setGroupCohesionStrength,
        setKickIntervalSec,
        setKickIntervalRandomnessSec,
        setSpawnChildIntervalSec,
        setSpawnChildIntervalRandomnessSec,
        setChildTrailCapMode,
        setChildTrailCapFixed,
        setChildTrailCapRandomness,
        setStopAtMargin,
        setForceMagnitude,
        setVelocityKickGradual,
        setVelocityKickBlendFrames,
        setStrokeColor,
        setStrokeWidth,
        setMinStrokeWidth,
        setInitialStrokeWidth,
        setStrokeWidthStep,
        setStrokeWidthKickFrames,
        setStrokeWidthKickGradual,
        setStrokeWidthBlendFrames,
        setTrailTaperStartLength,
        setTrailTaperEndLength,
        setSmoothPaths,
        setMergeTrailToSinglePath,
        setFilledVariableWidthStroke,
        setUnionGroupOnComplete,
        setBackgroundColor,
        setExportBackground,
        clearTrails,
        randomizeParameters,
        applyPreset,
        getPresetValues,
        resetParameters,
    } = useFlowAgents();

    const strokeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    const bgTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        return () => {
            if (strokeTimeout.current) clearTimeout(strokeTimeout.current);
            if (bgTimeout.current) clearTimeout(bgTimeout.current);
        };
    }, []);

    const handleStrokePicker = (value: string) => {
        if (strokeTimeout.current) clearTimeout(strokeTimeout.current);
        strokeTimeout.current = setTimeout(() => setStrokeColor(value), 150);
    };

    const handleBgPicker = (value: string) => {
        if (bgTimeout.current) clearTimeout(bgTimeout.current);
        bgTimeout.current = setTimeout(() => setBackgroundColor(value), 150);
    };

    const handleInt =
        (setter: (v: number) => void, min = 0, max = Infinity) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min || n > max) return;
            setter(Math.round(n));
        };

    const handleFloat =
        (setter: (v: number) => void, min = 0, max = Infinity) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min || n > max) return;
            setter(n);
        };

    return (
        <div className="panel-controls blob-field-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Flow Agents</h2>
                    <ToolDescriptionPopover dialogLabel="Flow Agents tool description">
                        <p>
                            A Perlin flow field is generated first. Toggle field
                            visibility, then click (or click-drag to set initial
                            direction) on the artboard to spawn agent(s) that
                            follow the field until trail length limit or the
                            canvas edge. With multiple agents per click, one is
                            the leader (gold ring) and the rest pull toward it.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                    <button
                        type="button"
                        className="generic-button"
                        onClick={randomizeParameters}
                    >
                        Randomize parameters
                    </button>
                    <button
                        type="button"
                        className="generic-button"
                        onClick={clearTrails}
                        disabled={
                            completedPaths.length === 0 &&
                            activeAgents.length === 0
                        }
                    >
                        Clear trails
                    </button>
                </div>
            </div>

            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="flow-agents"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />
                <CollapsibleToolGroup title="Artboard" icon={LayoutTemplate}>
                    <div className="field-row">
                        <label className="field-label">
                            Width
                            <DebouncedInput
                                type="number"
                                min={120}
                                max={2400}
                                step={10}
                                value={width}
                                onChange={handleInt(setWidth, 120)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Height
                            <DebouncedInput
                                type="number"
                                min={120}
                                max={2400}
                                step={10}
                                value={height}
                                onChange={handleInt(setHeight, 120)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Margin (× min side, extends field past artboard)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={2}
                                step={0.05}
                                value={marginFactor}
                                onChange={handleFloat(setMarginFactor, 0, 2)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Grid cell size
                            <DebouncedInput
                                type="number"
                                min={4}
                                max={80}
                                step={1}
                                value={cellSize}
                                onChange={handleInt(setCellSize, 4)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={stopAtMargin}
                            onChange={setStopAtMargin}
                            label="Stop agents at artboard edge"
                            title="When off, trails can continue past the artboard edge until max trail length or other limits."
                        />
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Field" icon={Wind}>
                    <div className="field-row">
                        <label className="field-label">
                            Seed
                            <DebouncedInput
                                type="text"
                                value={seed}
                                onChange={(v) => setSeed(String(v))}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Angle noise
                            <select
                                value={angleNoiseMode}
                                onChange={(e) =>
                                    setAngleNoiseMode(
                                        e.target.value as FlowAngleNoiseMode,
                                    )
                                }
                            >
                                {FLOW_ANGLE_NOISE_MODES.map((o) => (
                                    <option key={o.value} value={o.value}>
                                        {o.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                    {(angleNoiseMode === "fbm" ||
                        angleNoiseMode === "domain_warp") && (
                        <div className="field-row">
                            <label className="field-label">
                                FBM octaves (angle)
                                <DebouncedInput
                                    type="number"
                                    min={1}
                                    max={8}
                                    step={1}
                                    value={angleNoiseOctaves}
                                    onChange={handleInt(
                                        setAngleNoiseOctaves,
                                        1,
                                    )}
                                    debounceMs={400}
                                />
                            </label>
                        </div>
                    )}
                    {angleNoiseMode === "domain_warp" && (
                        <>
                            <div className="field-row">
                                <label className="field-label">
                                    Warp strength (noise space)
                                    <DebouncedInput
                                        type="number"
                                        min={0}
                                        max={2}
                                        step={0.05}
                                        value={angleWarpStrength}
                                        onChange={handleFloat(
                                            setAngleWarpStrength,
                                            0,
                                            2,
                                        )}
                                        debounceMs={400}
                                    />
                                </label>
                            </div>
                            <div className="field-row">
                                <label className="field-label">
                                    Warp frequency (× sampling coords)
                                    <DebouncedInput
                                        type="number"
                                        min={0.05}
                                        max={4}
                                        step={0.05}
                                        value={angleWarpFrequency}
                                        onChange={handleFloat(
                                            setAngleWarpFrequency,
                                            0.05,
                                            4,
                                        )}
                                        debounceMs={400}
                                    />
                                </label>
                            </div>
                        </>
                    )}
                    <div className="field-row">
                        <label className="field-label">
                            Noise scale (angle / grid sampling)
                            <DebouncedInput
                                type="number"
                                min={0.01}
                                max={0.5}
                                step={0.01}
                                value={noiseScale}
                                onChange={handleFloat(setNoiseScale, 0.01, 0.5)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            2nd noise scale (speed modulation)
                            <DebouncedInput
                                type="number"
                                min={0.01}
                                max={0.5}
                                step={0.01}
                                value={stepNoiseScale}
                                onChange={handleFloat(
                                    setStepNoiseScale,
                                    0.01,
                                    0.5,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Speed variation (2nd noise, 0–1)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={0.05}
                                value={stepVariation}
                                onChange={handleFloat(setStepVariation, 0, 1)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <p
                        style={{
                            fontSize: "0.75em",
                            opacity: 0.7,
                            margin: "0 0 8px",
                        }}
                    >
                        When speed variation is above 0, travel speed is
                        modulated by a second Perlin field (same as step
                        variation in Flow Field). Angle noise mode applies only
                        to direction; step noise stays single-layer Perlin.
                    </p>
                    <div className="field-row">
                        <label className="field-label">
                            Field rotation (°)
                            <DebouncedInput
                                type="number"
                                min={-180}
                                max={180}
                                step={1}
                                value={fieldRotationDeg}
                                onChange={handleFloat(
                                    setFieldRotationDeg,
                                    -180,
                                    180,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={mirrorNoiseX}
                            onChange={setMirrorNoiseX}
                            label="Mirror noise horizontally"
                        />
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={mirrorNoiseY}
                            onChange={setMirrorNoiseY}
                            label="Mirror noise vertically"
                        />
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Uniform flow blend (0–1)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={0.05}
                                value={uniformFlowBlend}
                                onChange={handleFloat(
                                    setUniformFlowBlend,
                                    0,
                                    1,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Uniform flow angle (°)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={360}
                                step={1}
                                value={uniformFlowAngleDeg}
                                onChange={handleFloat(
                                    setUniformFlowAngleDeg,
                                    0,
                                    360,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <p
                        style={{
                            fontSize: "0.75em",
                            opacity: 0.7,
                            margin: "0 0 8px",
                        }}
                    >
                        Rotate and mirror reshape the Perlin field; blend mixes
                        in a constant direction (0° = right).
                    </p>
                    <div className="field-row">
                        <label className="field-label">
                            Quantize angles (°)
                            <select
                                value={String(angleQuantizeDeg)}
                                onChange={(e) =>
                                    setAngleQuantizeDeg(Number(e.target.value))
                                }
                                style={{ marginTop: "4px", width: "100%" }}
                            >
                                <option value="0">Off (smooth)</option>
                                <option value="5">5°</option>
                                <option value="10">10°</option>
                                <option value="15">15°</option>
                                <option value="22.5">22.5°</option>
                                <option value="30">30°</option>
                                <option value="45">45°</option>
                            </select>
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={showFlowField}
                            onChange={setShowFlowField}
                            label="Show flow field (angle vectors)"
                            inline
                        />
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Agent motion" icon={Activity}>
                    <div className="field-row">
                        <label className="field-label">
                            Follow speed (px/s)
                            <DebouncedInput
                                type="number"
                                min={10}
                                max={2000}
                                step={5}
                                value={followSpeed}
                                onChange={handleInt(setFollowSpeed, 10)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Flow influence ({flowInfluence.toFixed(2)} — 0
                            inertial, 1 field-locked)
                            <input
                                type="range"
                                min={0}
                                max={1}
                                step={0.01}
                                value={flowInfluence}
                                onChange={(e) =>
                                    setFlowInfluence(Number(e.target.value))
                                }
                                style={{ width: "100%", marginTop: "6px" }}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Max trail length (px)
                            <DebouncedInput
                                type="number"
                                min={50}
                                max={50000}
                                step={50}
                                value={maxTrailLength}
                                onChange={handleInt(setMaxTrailLength, 50)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Trail length randomness (0–1, ± fraction of max)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={0.05}
                                value={maxTrailLengthRandomness}
                                onChange={handleFloat(
                                    setMaxTrailLengthRandomness,
                                    0,
                                    1,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label
                            className="field-label"
                            title="0 = off. Agents periodically spawn a new agent at their position (same spawn group)."
                        >
                            Spawn child interval (seconds)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={60}
                                step={0.05}
                                value={spawnChildIntervalSec}
                                onChange={handleFloat(
                                    setSpawnChildIntervalSec,
                                    0,
                                    60,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label
                            className="field-label"
                            title="± seconds jitter around the spawn interval."
                        >
                            Spawn interval randomness (± seconds)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={30}
                                step={0.05}
                                value={spawnChildIntervalRandomnessSec}
                                onChange={handleFloat(
                                    setSpawnChildIntervalRandomnessSec,
                                    0,
                                    30,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label
                            className="field-label"
                            title="How to set max trail length for spawned agents. None uses the global max trail length and its randomness."
                        >
                            Child trail cap
                            <select
                                value={childTrailCapMode}
                                onChange={(e) =>
                                    setChildTrailCapMode(
                                        e.target.value as ChildTrailCapMode,
                                    )
                                }
                                disabled={spawnChildIntervalSec <= 0}
                                aria-label="Child trail cap mode"
                            >
                                <option value="none">
                                    None (global max ± randomness)
                                </option>
                                <option value="parentRemaining">
                                    Parent&apos;s remaining length
                                </option>
                                <option value="fixed">Fixed cap (number)</option>
                            </select>
                        </label>
                    </div>
                    {childTrailCapMode === "fixed" && (
                        <div className="field-row">
                            <label
                                className="field-label"
                                title="Base cap in SVG units before child trail cap randomness is applied."
                            >
                                Child trail cap (px)
                                <DebouncedInput
                                    type="number"
                                    min={50}
                                    max={50000}
                                    step={50}
                                    value={childTrailCapFixed}
                                    onChange={handleInt(
                                        setChildTrailCapFixed,
                                        50,
                                        50000,
                                    )}
                                    debounceMs={400}
                                    disabled={spawnChildIntervalSec <= 0}
                                />
                            </label>
                        </div>
                    )}
                    <div className="field-row">
                        <label
                            className="field-label"
                            title="0 = fixed sampled cap; 1 = ±100% jitter around the base cap for this mode."
                        >
                            Child trail cap randomness (0–1)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={0.05}
                                value={childTrailCapRandomness}
                                onChange={handleFloat(
                                    setChildTrailCapRandomness,
                                    0,
                                    1,
                                )}
                                debounceMs={400}
                                disabled={spawnChildIntervalSec <= 0}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Kick interval (seconds)
                            <DebouncedInput
                                type="number"
                                min={0.25}
                                max={60}
                                step={0.25}
                                value={kickIntervalSec}
                                onChange={handleFloat(
                                    setKickIntervalSec,
                                    0.25,
                                    60,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Interval randomness (± seconds)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={30}
                                step={0.05}
                                value={kickIntervalRandomnessSec}
                                onChange={handleFloat(
                                    setKickIntervalRandomnessSec,
                                    0,
                                    30,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Kick force magnitude
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={2000}
                                step={5}
                                value={forceMagnitude}
                                onChange={handleFloat(
                                    setForceMagnitude,
                                    0,
                                    2000,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={velocityKickGradual}
                            onChange={setVelocityKickGradual}
                            label="Gradual velocity kicks"
                            title="Spread each kick’s velocity change evenly over several frames instead of applying it in one step."
                        />
                    </div>
                    {velocityKickGradual && (
                        <div className="field-row">
                            <label className="field-label">
                                Kick blend (frames)
                                <DebouncedInput
                                    type="number"
                                    min={1}
                                    max={120}
                                    step={1}
                                    value={velocityKickBlendFrames}
                                    onChange={handleInt(
                                        setVelocityKickBlendFrames,
                                        1,
                                        120,
                                    )}
                                    debounceMs={400}
                                />
                            </label>
                        </div>
                    )}
                    <p
                        style={{
                            fontSize: "0.85em",
                            opacity: 0.75,
                            margin: "0",
                        }}
                    >
                        Random impulse added on each kick (clamped like path
                        agents). Set force to 0 to disable.
                    </p>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup
                    title="Artboard input"
                    icon={MousePointer2}
                >
                    <div className="field-row field-row--switch">
                        <Switch
                            checked={inputMode === "dragPaint"}
                            onChange={(on) =>
                                setInputMode(on ? "dragPaint" : "clickSpawn")
                            }
                            label="Drag to paint (puppet group)"
                            title="Off: pointer up spawns; short drag = point, longer drag sets direction. On: pointer down spawns; hold to pull the leader toward the cursor."
                        />
                    </div>
                    <p
                        style={{
                            fontSize: "0.85em",
                            opacity: 0.75,
                            margin: "0.35rem 0 0",
                        }}
                    >
                        {inputMode === "clickSpawn"
                            ? "Click / drag direction to spawn."
                            : "Paint by holding and moving; release to let agents run freely."}
                    </p>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Brush" icon={Paintbrush}>
                    <p
                        style={{
                            fontSize: "0.85em",
                            opacity: 0.8,
                            margin: "0 0 0.5rem",
                        }}
                    >
                        Parallel stroke lanes multiply with agents per click in
                        the Groups section.
                    </p>
                    <div className="field-row">
                        <label
                            className="field-label"
                            title="Parallel paths per group position. Multiplies with agents per click."
                        >
                            Stroke layout (parallel)
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={32}
                                step={1}
                                value={strokeLayoutCount}
                                onChange={handleInt(
                                    setStrokeLayoutCount,
                                    1,
                                    32,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label
                            className="field-label"
                            title="Spacing between parallel stroke starts, perpendicular to motion (SVG px)."
                        >
                            Layout spacing
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={400}
                                step={1}
                                value={strokeLayoutSpacing}
                                onChange={handleFloat(
                                    setStrokeLayoutSpacing,
                                    0,
                                    400,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Groups" icon={Users}>
                    <div className="field-row">
                        <label className="field-label">
                            Agents per click
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={32}
                                step={1}
                                value={groupSize}
                                onChange={handleInt(setGroupSize, 1, 32)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Group radius
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={999}
                                step={1}
                                value={groupRadius}
                                onChange={handleFloat(setGroupRadius, 1, 999)}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Cohesion (follow strength)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={40}
                                step={0.5}
                                value={groupCohesionStrength}
                                onChange={handleFloat(
                                    setGroupCohesionStrength,
                                    0,
                                    40,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={unionGroupOnComplete}
                            onChange={setUnionGroupOnComplete}
                            label="Unite trails when group completes"
                            title="Default: like Illustrator Pathfinder Unite — outline trails to filled regions, boolean-union per spawn group when the last agent finishes; one merged path per group in SVG."
                        />
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Stroke trail" icon={Spline}>
                    <div className="field-row">
                        <CheckboxField
                            checked={smoothPaths}
                            onChange={setSmoothPaths}
                            label="Smooth paths (curves)"
                        />
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={mergeTrailToSinglePath}
                            onChange={setMergeTrailToSinglePath}
                            label="Single path for varying width"
                            title="When stroke width changes along the trail, draw one path with average width instead of one line per segment."
                        />
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={filledVariableWidthStroke}
                            onChange={setFilledVariableWidthStroke}
                            label="Filled variable-width stroke"
                            title="Filled outline from per-vertex widths (true variable-width stroke)."
                        />
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Taper start (px)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={2400}
                                step={1}
                                value={trailTaperStartLength}
                                onChange={handleFloat(
                                    setTrailTaperStartLength,
                                    0,
                                    2400,
                                )}
                                debounceMs={400}
                            />
                        </label>
                        <label className="field-label">
                            Taper end (px)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={2400}
                                step={1}
                                value={trailTaperEndLength}
                                onChange={handleFloat(
                                    setTrailTaperEndLength,
                                    0,
                                    2400,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Stroke width" icon={Ruler}>
                    <div className="field-row">
                        <label className="field-label">
                            Min stroke width
                            <DebouncedInput
                                type="number"
                                min={0.05}
                                max={40}
                                step={0.05}
                                value={minStrokeWidth}
                                onChange={handleFloat(
                                    setMinStrokeWidth,
                                    0.05,
                                    40,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Initial stroke width
                            <DebouncedInput
                                type="number"
                                min={0.25}
                                max={40}
                                step={0.25}
                                value={initialStrokeWidth}
                                onChange={handleFloat(
                                    setInitialStrokeWidth,
                                    minStrokeWidth,
                                    strokeWidth,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Max stroke width
                            <DebouncedInput
                                type="number"
                                min={0.25}
                                max={40}
                                step={0.25}
                                value={strokeWidth}
                                onChange={handleFloat(
                                    setStrokeWidth,
                                    minStrokeWidth,
                                    40,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Stroke width step (± per kick)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={20}
                                step={0.05}
                                value={strokeWidthStep}
                                onChange={handleFloat(
                                    setStrokeWidthStep,
                                    0,
                                    20,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Stroke width kick (frames)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={600}
                                step={1}
                                value={strokeWidthKickFrames}
                                onChange={handleInt(
                                    setStrokeWidthKickFrames,
                                    0,
                                    600,
                                )}
                                debounceMs={400}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={strokeWidthKickGradual}
                            onChange={setStrokeWidthKickGradual}
                            label="Gradual stroke width kicks"
                            title="Spread each width change evenly over several frames."
                        />
                    </div>
                    {strokeWidthKickGradual && (
                        <div className="field-row">
                            <label className="field-label">
                                Width kick blend (frames)
                                <DebouncedInput
                                    type="number"
                                    min={1}
                                    max={120}
                                    step={1}
                                    value={strokeWidthBlendFrames}
                                    onChange={handleInt(
                                        setStrokeWidthBlendFrames,
                                        1,
                                        120,
                                    )}
                                    debounceMs={400}
                                />
                            </label>
                        </div>
                    )}
                    <p
                        style={{
                            fontSize: "0.85em",
                            opacity: 0.75,
                            margin: "0",
                        }}
                    >
                        Live width is clamped between min and max; kicks use ±
                        step. Set kick interval to 0 to disable width kicks.
                    </p>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Appearance" icon={Palette}>
                    <div className="field-row">
                        <label className="field-label">
                            Stroke
                            <div
                                style={{
                                    display: "flex",
                                    gap: "8px",
                                    alignItems: "center",
                                    marginTop: "4px",
                                }}
                            >
                                <input
                                    type="color"
                                    value={strokeColor}
                                    onChange={(e) =>
                                        handleStrokePicker(e.target.value)
                                    }
                                    style={{
                                        width: "50px",
                                        height: "30px",
                                        minWidth: "30px",
                                    }}
                                />
                                <DebouncedInput
                                    type="text"
                                    value={strokeColor}
                                    onChange={(v) => setStrokeColor(String(v))}
                                    style={{
                                        flex: 1,
                                        padding: "4px",
                                        fontFamily: "monospace",
                                    }}
                                />
                            </div>
                        </label>
                    </div>
                    <div className="field-row">
                        <label
                            className="field-label"
                            title="Extra colors for random stroke mode. When random off, only the main stroke color is used."
                        >
                            Stroke palette
                            <ColorPaletteEditor
                                colors={parsePaletteTextToColors(
                                    strokePaletteText,
                                )}
                                onChange={(c) =>
                                    setStrokePaletteText(colorsToPaletteText(c))
                                }
                                defaultNewColor={strokeColor}
                                description="Add colors below; each new agent can pick randomly from this list when enabled."
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={randomStrokeColorPerAgent}
                            onChange={setRandomStrokeColorPerAgent}
                            label="Random stroke color per agent (from palette)"
                            title="Each new agent picks a random color from the palette (or the main stroke color if the palette is empty)."
                        />
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Background
                            <div
                                style={{
                                    display: "flex",
                                    gap: "8px",
                                    alignItems: "center",
                                    marginTop: "4px",
                                }}
                            >
                                <input
                                    type="color"
                                    value={backgroundColor}
                                    onChange={(e) =>
                                        handleBgPicker(e.target.value)
                                    }
                                    style={{
                                        width: "50px",
                                        height: "30px",
                                        minWidth: "30px",
                                    }}
                                />
                                <DebouncedInput
                                    type="text"
                                    value={backgroundColor}
                                    onChange={(v) =>
                                        setBackgroundColor(String(v))
                                    }
                                    style={{
                                        flex: 1,
                                        padding: "4px",
                                        fontFamily: "monospace",
                                    }}
                                />
                            </div>
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={exportBackground}
                            onChange={setExportBackground}
                            label="Include background in export"
                            inline
                        />
                    </div>
                </CollapsibleToolGroup>
            </div>
        </div>
    );
}
